import {
  ASSISTANT_ATTACHMENT_LIMITS,
  ASSISTANT_IMAGE_TYPES,
  type AssistantAttachment,
  type AssistantImageType,
  type CreateAssistantAttachmentRequest,
} from "@cloudserver/contracts";
import { base64Of } from "./screens.js";

/*
 * What the person attaches in the chat's composer (docs/assistant.md,
 * "Attachments"): a log as text (picked, or a long paste), a screenshot as
 * an image (picked or pasted). Checked here for type and size so a person
 * learns at once; the API checks again and redacts logs.
 */

export interface PendingAttachment {
  localId: string;
  name: string;
  kind: "log" | "image";
  status: "uploading" | "ready" | "failed";
  attachment: AssistantAttachment | null;
  /** The reason it was refused (`assistant_attachment_too_large`…). */
  problem: string | null;
  /** An object URL of a picture, for its thumbnail. */
  preview: string | null;
}

/** A paste this long goes in as a log instead of into the message. */
export const PASTE_AS_LOG = { chars: 2000, lines: 30 } as const;

export function isLongPaste(text: string): boolean {
  return text.length >= PASTE_AS_LOG.chars || text.split("\n").length >= PASTE_AS_LOG.lines;
}

/** File names a log has; files without a type are read as text when their name says so. */
const LOG_NAME = /\.(log|txt|out|err|json|ya?ml|conf|cfg|ini|toml|env|csv|xml|md|sh|service|sql)$/i;

export function isImageType(type: string): type is AssistantImageType {
  return (ASSISTANT_IMAGE_TYPES as readonly string[]).includes(type);
}

export function isTextFile(file: Pick<File, "type" | "name">): boolean {
  return (
    file.type.startsWith("text/") ||
    file.type === "application/json" ||
    file.type === "application/x-yaml" ||
    ((file.type === "" || file.type === "application/octet-stream") && LOG_NAME.test(file.name))
  );
}

type Prepared =
  | {
      name: string;
      kind: "log" | "image";
      preview: string | null;
      body: CreateAssistantAttachmentRequest;
    }
  | { name: string; kind: "log" | "image"; preview: null; problem: string };

/** A picked or pasted file as the API takes it, or why it cannot go. */
export async function prepareFile(file: File, source: "file" | "paste"): Promise<Prepared> {
  const name = (file.name || (isImageType(file.type) ? "screenshot.png" : "log.txt")).slice(0, 200);
  if (isImageType(file.type)) {
    if (file.size > ASSISTANT_ATTACHMENT_LIMITS.imageBytes)
      return { name, kind: "image", preview: null, problem: "assistant_attachment_too_large" };
    return {
      name,
      kind: "image",
      preview: URL.createObjectURL(file),
      body: { kind: "image", source, name, mediaType: file.type, data: await base64Of(file) },
    };
  }
  if (!isTextFile(file))
    return { name, kind: "log", preview: null, problem: "assistant_attachment_type" };
  const text = await file.text();
  if (text.length > ASSISTANT_ATTACHMENT_LIMITS.logChars)
    return { name, kind: "log", preview: null, problem: "assistant_attachment_too_large" };
  if (text.trim() === "")
    return { name, kind: "log", preview: null, problem: "assistant_attachment_empty" };
  return { name, kind: "log", preview: null, body: { kind: "log", source, name, text } };
}

/** «12 КБ»: a size for the attachment's chip. */
export function sizeText(bytes: number, language: string): string {
  const units = ["B", "KB", "MB"] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const number = new Intl.NumberFormat(language, {
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(value);
  const ru = { B: "Б", KB: "КБ", MB: "МБ" } as const;
  return `${number} ${language.startsWith("ru") ? ru[units[unit] ?? "B"] : units[unit]}`;
}
